import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { BudgetPeriodType } from '@/api/budget'
import { cn } from '@/lib/utils'
import { formatPeriodLabel, shiftPeriod, type FyStartMonth } from '@/lib/budgetPeriod'

const PERIOD_TYPES: { value: BudgetPeriodType; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'HALF_YEARLY', label: 'Half-Yearly' },
  { value: 'YEARLY', label: 'Yearly' },
]

export function BudgetPeriodControls({
  periodType,
  period,
  fyStartMonth,
  disabled = false,
  onPeriodTypeChange,
  onPeriodChange,
}: {
  periodType: BudgetPeriodType
  fyStartMonth: FyStartMonth
  /** Until the household's year setting is known, a quarter or year would be guessed. */
  disabled?: boolean
  period: string
  onPeriodTypeChange: (type: BudgetPeriodType) => void
  onPeriodChange: (period: string) => void
}) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div
        role="radiogroup"
        aria-label="Budget period granularity"
        className="inline-flex items-center gap-1 rounded-full bg-[var(--fl-surface-subtle)] border border-[var(--fl-border)] p-1"
      >
        {PERIOD_TYPES.map((p) => {
          const selected = p.value === periodType
          return (
            <button
              key={p.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onPeriodTypeChange(p.value)}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selected
                  ? 'bg-[var(--fl-surface)] text-[var(--fl-primary-text)] shadow-sm'
                  : 'text-[var(--fl-text-2)] hover:text-[var(--fl-text-1)]'
              )}
            >
              {p.label}
            </button>
          )
        })}
      </div>
      <div className="inline-flex items-center rounded-lg border border-[var(--fl-border)] bg-[var(--fl-surface)]">
        <button
          type="button"
          aria-label="Previous period"
          disabled={disabled}
          onClick={() => onPeriodChange(shiftPeriod(periodType, period, -1, fyStartMonth))}
          className="p-2 text-[var(--fl-text-2)] hover:text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-subtle)] rounded-l-lg transition-colors disabled:pointer-events-none disabled:opacity-40"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="px-2 text-sm font-medium text-[var(--fl-text-1)] min-w-[10rem] text-center">
          {formatPeriodLabel(periodType, period, fyStartMonth)}
        </span>
        <button
          type="button"
          aria-label="Next period"
          disabled={disabled}
          onClick={() => onPeriodChange(shiftPeriod(periodType, period, 1, fyStartMonth))}
          className="p-2 text-[var(--fl-text-2)] hover:text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-subtle)] rounded-r-lg transition-colors disabled:pointer-events-none disabled:opacity-40"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  )
}
